import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { createColumnHelper } from '@tanstack/react-table';
import Routes from '@/router/Routes.ts';
import useOrgCredits from '@/api/useOrgCredits.ts';
import useOrgDecisions, { type OrgDecisionRow } from '@/api/useOrgDecisions.ts';
import DataTable from '@/components/functional/DataTable/DataTable.tsx';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import { FRAMEWORK_BY_ID, type FrameworkId } from '@/constants/frameworks.ts';
import {
  buildSessionDetailNavigation,
  displayStatusStyle,
  formatGovScore,
  isSessionFlagged,
  resolveSessionAction,
  resolveSessionOpenRoute,
} from '@/utils/sessionTableDisplay.ts';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/classNames.ts';
import { ViewfinderCircleIcon } from '@heroicons/react/24/outline';

dayjs.extend(relativeTime);

const columnHelper = createColumnHelper<OrgDecisionRow>();

const INTAKE_DRAFT_STORAGE_KEY = 'command:common-intake-draft';

const MISSING = '—';

function frameworkLabel(frameworkId: string | null): string {
  if (!frameworkId) return MISSING;
  return FRAMEWORK_BY_ID[frameworkId as FrameworkId]?.label ?? frameworkId;
}

function readLocalIntakeDraftTitle(): string | null {
  try {
    const raw = localStorage.getItem(INTAKE_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { data?: { issue_title?: string } };
    return parsed?.data?.issue_title?.trim() || null;
  } catch {
    return null;
  }
}

const HomePage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { creditsRemaining, hasCredits, isLoading, isError } = useOrgCredits();
  const { data: sessions = [], isLoading: isSessionsLoading } = useOrgDecisions();
  const localDraftTitle = useMemo(() => readLocalIntakeDraftTitle(), []);

  const [summaryPreview, setSummaryPreview] = useState<{ title: string; body: string } | null>(null);
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);

  const flaggedSessions = useMemo(() => sessions.filter(isSessionFlagged), [sessions]);
  const flaggedCount = flaggedSessions.length;
  const tableSessions = showFlaggedOnly ? flaggedSessions : sessions;

  const columns = useMemo(
    () => [
      columnHelper.accessor('issue_title', {
        header: () => t('homePage.columnSessionName'),
        cell: (info) => (
          <span className="font-dm text-sm font-bold text-black/87">
            {info.getValue()?.trim() || MISSING}
          </span>
        ),
      }),
      columnHelper.accessor('primary_framework', {
        header: () => t('homePage.columnFramework'),
        cell: (info) => (
          <span className="font-dm text-sm text-black/70">{frameworkLabel(info.getValue())}</span>
        ),
        enableSorting: false,
      }),
      columnHelper.accessor('governance_score', {
        header: () => t('homePage.columnGovScore'),
        cell: (info) => (
          <span className="font-dm text-sm font-bold text-black/87">{formatGovScore(info.getValue())}</span>
        ),
        enableSorting: true,
      }),
      columnHelper.accessor('display_status', {
        header: () => t('homePage.columnStatus'),
        cell: (info) => {
          const status = info.getValue();
          const { labelKey, className } = displayStatusStyle(status);
          return <span className={className}>{t(labelKey)}</span>;
        },
      }),
      columnHelper.display({
        id: 'summary',
        header: () => t('homePage.columnSummary'),
        enableSorting: false,
        cell: ({ row }) => {
          const summary = row.original.structured_summary;
          const title = row.original.issue_title?.trim() || t('homePage.columnSessionName');

          if (!summary) {
            return <span className="font-dm text-sm text-black/35">{MISSING}</span>;
          }

          const framework = frameworkLabel(row.original.primary_framework);
          const modalTitle =
            framework !== MISSING ? `${title} (${framework})` : title;

          return (
            <button
              type="button"
              className="font-dm text-sm font-bold text-[#0F1B2A] underline underline-offset-2"
              onClick={() => setSummaryPreview({ title: modalTitle, body: summary })}
            >
              {t('homePage.readSummary')}
            </button>
          );
        },
      }),
      columnHelper.accessor('last_activity_at', {
        id: 'last_updated',
        header: () => t('homePage.columnLastUpdated'),
        cell: (info) => (
          <span className="font-dm text-sm text-black/60">{dayjs(info.getValue()).fromNow()}</span>
        ),
      }),
      columnHelper.display({
        id: 'action',
        header: () => t('homePage.columnAction'),
        enableSorting: false,
        cell: ({ row }) => {
          const {
            id,
            lifecycle_status,
            pending_review,
            primary_framework,
            governance_score,
          } = row.original;
          const action = resolveSessionAction(lifecycle_status, pending_review);
          const frameworkAttribution =
            (primary_framework && FRAMEWORK_BY_ID[primary_framework as FrameworkId]?.label) ||
            primary_framework ||
            undefined;

          if (action === 'open_session') {
            return (
              <button
                type="button"
                className="font-dm text-sm font-bold text-[#0F1B2A] underline underline-offset-2"
                onClick={() =>
                  navigate(resolveSessionOpenRoute(lifecycle_status), { state: { decisionId: id } })
                }
              >
                {t('homePage.openSession')}
              </button>
            );
          }

          if (action === 'view_record') {
            const { pathname, state } = buildSessionDetailNavigation(id);
            return (
              <button
                type="button"
                className="font-dm text-sm font-bold text-[#0F1B2A] underline underline-offset-2"
                onClick={() => navigate(pathname, { state })}
              >
                {t('homePage.reviewSession')}
              </button>
            );
          }

          if (action === 'complete_session') {
            return (
              <button
                type="button"
                className="font-dm text-sm font-bold text-[#0F1B2A] underline underline-offset-2"
                onClick={() =>
                  navigate(Routes.RECOMMENDATION_ENGINE, {
                    state: {
                      decisionId: id,
                      resumeStep: 'compass-review',
                      frameworkAttribution,
                      governanceScore: governance_score,
                    },
                  })
                }
              >
                {t('homePage.completeSession')}
              </button>
            );
          }

          return <span className="font-dm text-sm font-bold text-black/35">{MISSING}</span>;
        },
      }),
    ],
    [navigate, t],
  );

  const showSessionsEmptyHint = !isSessionsLoading && sessions.length === 0;

  return (
    <div className="flex flex-col gap-8 h-full min-h-[calc(100vh-56px)] lg:min-h-full">
      <div className="flex flex-row items-center justify-between gap-2 px-6 py-4 bg-[#F4F9FF] rounded-xl">
        <div className="flex flex-col gap-1">
          <h1 className="font-newsreader text-[28px] font-extrabold leading-[125%] text-[rgba(0,0,0,0.87)]">
            COMMAND Executive Surface™
          </h1>
          <p className="font-dm text-[14px] font-normal leading-[150%] tracking-[0.15px] text-[rgba(0,0,0,0.6)]">
            {t('homePage.subtitle')}
          </p>
          {!isLoading && !isError && hasCredits && (
            <p className="font-dm text-[13px] leading-[150%] text-[rgba(0,0,0,0.5)]">
              {t('homePage.creditsRemaining', { count: creditsRemaining })}
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-2 shrink-0">
          <button
            type="button"
            className={cn(
              'font-dm px-[22px] py-2 rounded-xl text-[#FAFAFA] font-bold text-[14px] leading-[26px] tracking-[0.46px] whitespace-nowrap',
              hasCredits && !isLoading ? 'bg-[#0F1B2A]' : 'bg-[#0F1B2A]/40 cursor-not-allowed',
            )}
            disabled={isLoading || !hasCredits}
            onClick={() => navigate(Routes.COMMON_INTAKE)}
          >
            + {t('homePage.startSession')}
          </button>
          {!isLoading && !hasCredits && (
            <>
              <p className="font-dm text-[12px] text-right text-[rgba(0,0,0,0.55)] max-w-[220px]">
                {t('homePage.noCreditsHint')}
              </p>
              <button
                type="button"
                className="font-dm text-[13px] font-bold text-[#0F1B2A] underline underline-offset-2"
                onClick={() => navigate(Routes.CREDITS)}
              >
                {t('homePage.purchaseCredits')}
              </button>
            </>
          )}
        </div>
      </div>

      {localDraftTitle && (
        <div className="flex flex-row items-center justify-between gap-4 px-6 py-3 bg-[#FFF8E7] rounded-xl border border-[rgba(0,0,0,0.08)]">
          <p className="font-dm text-sm text-black/70">
            {t('homePage.localDraftBanner', { title: localDraftTitle })}
          </p>
          <button
            type="button"
            className="font-dm text-sm font-bold text-[#0F1B2A] underline underline-offset-2 shrink-0"
            onClick={() => navigate(Routes.COMMON_INTAKE)}
          >
            {t('homePage.continueLocalDraft')}
          </button>
        </div>
      )}

      <div className="flex flex-col gap-4 flex-1">
        {isSessionsLoading ? (
          <LoaderWrapper isLoading />
        ) : (
          <>
            {flaggedCount > 0 && (
              <div className="flex flex-col gap-3 px-6 py-5 rounded-2xl border border-base-300 bg-base-100">
                <div className="flex items-center gap-2">
                  <ViewfinderCircleIcon className="w-5 h-5 text-[#0F1B2A] shrink-0" aria-hidden />
                  <h2 className="font-newsreader text-xl font-extrabold text-[rgba(0,0,0,0.87)]">
                    {t('homePage.currentOrientation.title')}
                  </h2>
                </div>
                <p className="font-dm text-sm text-black/60 leading-relaxed">
                  {t('homePage.currentOrientation.description', { count: flaggedCount })}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    className={cn(
                      'font-dm px-[22px] py-2 rounded-xl text-[#FAFAFA] font-bold text-sm tracking-[0.46px]',
                      showFlaggedOnly ? 'bg-[#0F1B2A]/80' : 'bg-[#0F1B2A]',
                    )}
                    onClick={() => setShowFlaggedOnly(true)}
                  >
                    {t('homePage.currentOrientation.reviewFlagged')}
                  </button>
                </div>
              </div>
            )}

            {showFlaggedOnly && (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 font-dm text-sm font-bold text-[#0F1B2A] w-fit"
                onClick={() => setShowFlaggedOnly(false)}
              >
                <ChevronLeftIcon className="w-4 h-4 shrink-0" aria-hidden />
                {t('homePage.currentOrientation.backToAll')}
              </button>
            )}

            {tableSessions.length > 0 && (<DataTable
              data={tableSessions}
              columns={columns}
              zebra
              className="table-auto"
              rowClassName="h-auto"
              wrapperClassName="w-full grow-0 h-auto max-h-[min(480px,calc(100vh-320px))] rounded-2xl border border-base-300 bg-base-100"
              initialState={{ sorting: [{ id: 'last_updated', desc: true }] }}
            />
)}
            {showSessionsEmptyHint && !showFlaggedOnly && (
              <div className="flex flex-1 items-center justify-center py-12">
                <div className="rounded-2xl bg-[#F4F9FF] px-8 py-10 w-full max-w-xl text-center">
                  <p className="font-dm text-sm text-black/70 leading-relaxed">
                    {hasCredits ? t('homePage.emptyState') : t('homePage.emptyStateNoCredits')}
                  </p>
                </div>
              </div>
            )}

            {showFlaggedOnly && tableSessions.length === 0 && (
              <p className="font-dm text-sm text-center text-black/50 px-4">
                {t('homePage.emptyState')}
              </p>
            )}
          </>
        )}
      </div>

      {summaryPreview && (
        <dialog open className="modal modal-open">
          <div className="modal-box max-w-lg font-dm">
            <h3 className="font-bold text-lg text-black/87">{summaryPreview.title}</h3>
            <p className="py-4 text-sm text-black/70 leading-relaxed whitespace-pre-wrap">{summaryPreview.body}</p>
            <div className="modal-action">
              <button type="button" className="btn btn-primary" onClick={() => setSummaryPreview(null)}>
                {t('button.close')}
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button type="button" onClick={() => setSummaryPreview(null)}>
              {t('button.close')}
            </button>
          </form>
        </dialog>
      )}
    </div>
  );
};

export default HomePage;
